import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide } from '../hooks/useCMS'

export function KropsterapiPage() {
  const { side: cmsSide } = useCMSSide('kropsterapi')

  useSEO({
    title: cmsSide?.seo_titel || 'Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'FORANDRING INDEFRA - INDTRYK som ikke imødekommes af et modsvarende UDTRYK, sætter sig som TRYK I KROPPEN',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Kerneområde · Totum Kropsterapi
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Kropsterapi'}
          </h1>
          <p className="mt-4 text-xl sm:text-2xl font-serif text-[#1b3a4b]/90 max-w-3xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'FORANDRING INDEFRA — INDTRYK som ikke imødekommes af et modsvarende UDTRYK, sætter sig som TRYK I KROPPEN'}
          </p>
        </div>
      </section>

      {/* Forløbs-princip Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10">
          <div className="flex items-center gap-3 text-[#7c875a] font-semibold text-xs uppercase tracking-wider mb-2">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>Vigtigt om tilgangen</span>
          </div>
          <h2 className="font-serif text-[21px] sm:text-3xl font-bold text-white mb-3">
            Jeg tilbyder behandlingsforløb
          </h2>
          <p className="text-[#FAF8F5]/90 text-sm sm:text-base leading-relaxed">
            ...og kun enkeltbehandlinger som opfølgning på forløb.
          </p>
          <p className="mt-3 text-sm text-[#FAF8F5]/80 leading-relaxed">
            Årsagen til dette er at jeg ønsker at hjælpe dig til at arbejde i dybden, og finde ind til de underliggende årsager der har forårsaget det symptomer, der mærkes som f.eks. følelsesmæssige blokeringer, fordøjelsesproblemer, søvnbesvær, selvværdsproblematikker, adfærdsproblemer og grænsesætning.
          </p>
          <div className="mt-6 pt-4 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
            <span className="text-xs text-[#7c875a]">Arbejd med årsagen, ikke kun symptomet</span>
            <a
              href="#kontakt-sektion"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#7c875a] text-[#132835] hover:bg-[#6a744c] px-5 py-2.5 rounded-full text-xs font-semibold transition text-center"
            >
              <span>Hør om et forløb</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Main Verbatim Scrape Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-12">
          {/* Hvem er det for */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Hvem henvender behandlingen sig til?
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Behandling hos mig er for dig, der ønsker hjælp til at bearbejde mentalt og følelsesmæssig stress, der opleves som ubalance, tankemylder, og smerter i kroppen.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Det er for dig som ønsker en helhedsorienteret tilgang i arbejdet med de oplevelser du har med i bagagen, som holder dig tilbage fra at leve det liv du ønsker dig at leve.
            </p>
          </div>

          {/* Forundersøgelse */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
              Første skridt
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
              FORUNDERSØGELSE
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ethvert forløb starter med en grundig forundersøgelse, hvor vi gennemgår dine udfordringer, og de oplevelser som har ført dig frem til hvor du er i dag. Under forundersøgelsen kommer vi ind på hvordan du hidtil har bearbejde disse oplevelser, og hvilken effekt du har oplevet af denne tilgang.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Hvis du tidligere har modtaget professionel hjælp, vil vi også tale om hvordan du har oplevet den måde at arbejde med dig selv på, og vi vil yderligere komme til at tale om din arbejds- og familiemæssige baggrund og nærmeste relationer.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Forundersøgelsen er delt mellem samtale og fysisk undersøgelse/behandling på briksen.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Til sidst i konsultationen får du et proffesionelt tilbud om et behandlingsforløb, som er tilpasse dig og dine udfordringer, med en plan for hvordan jeg bedst muligt kan hjælpe dig videre mod det liv du ønsker dig.
            </p>
            <p className="text-xs text-[#64748B] italic">
              Alle oplysninger bliver behandlet fortroligt, og brugt til at planlægge den videre behandling.
            </p>
          </div>

          {/* Før første session & Inden behandlingen */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E0D8] space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center font-bold text-sm">
                1
              </div>
              <h4 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Før første session
              </h4>
              <p className="text-sm text-[#475569] leading-relaxed">
                Når du booker tid til første session, modtager du en mail med et link til et spørgeskema som du bedes udfylde før du møder op første gang.
              </p>
              <p className="text-xs text-[#64748B] leading-relaxed">
                Spørgeskemaet er en problemspecifik og individualiseret målemetode, hvor du selv vurdere de to vigtigste symptomer forbundet med et specifikt problem, og en aktivitet i dagligdagen, der er påvirket af disse symptomer. Dine svar bruges til at sætte retning for behandlingen og giver mulighed for at evaluere effekten.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E0D8] space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#1b3a4b]/10 text-[#1b3a4b] flex items-center justify-center font-bold text-sm">
                2
              </div>
              <h4 className="font-serif text-xl font-bold text-[#1b3a4b]">
                Inden behandlingen
              </h4>
              <p className="text-sm text-[#475569] leading-relaxed">
                Første session starter med en samtale, hvor du fortæller om din problemstilling. Jeg stiller uddybende spørgsmål til dine oplevelse og historik, og spørger måske også ind til andre forhold som eks. familie-relationer.
              </p>
              <p className="text-xs text-[#64748B] leading-relaxed">
                Grunden til denne tilgang til behandlingen er udfra et holistisk livssyn, hvor samspillet mellem krop, sind og tanker er i fokus. Forudsætningerne for optimalt udbytte er en ærlig, åben, nærværende og omsorgsfuld kontakt.
              </p>
            </div>
          </div>

          {/* Under behandlingen */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Under behandlingen
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Under behandlingen ligger du i dit undertøj, på briksen under et tæppe. For kvinder er det en fordel med en bh som kan åbnes, når ryggen behandles. Jeg arbejder på hele kroppen for at skabe flow gennem musklerne så blodet, lymfevæsken og livsenergien Qi kan flyde frit. Behandlingen hjælper kroppen til at genfinde balancen, så den bliver i stand til at heale og regenerere sig selv.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              I forhold til mere klassiske massagemetoder er du ved TOTUM Kropsterapi selv en aktiv part i behandlingen, og du har selv stor indflydelse på effekten af behandlingen. Jeg guider dig i vejrtrækningen og til at arbejde sammen med din krop - mærke efter og fokusere på hvad, som sker i kroppen under behandlingen. Med pulserende bevægelser, tryk på spændte muskler og akupressurpunkter, zoneterapi, lettere ledfrigørelse og guidende spørgsmål arbejder vi sammen. Jo bedre du slipper, åbner op og giver plads, jo dybere kan vi sammen arbejde med at sætte smerten og kroppen fri af gamle fastgroede overbevisninger og spændinger.
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-4 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
              Det er ikke sjælent at klienter oplever at de i hovedet og tankerne har lyst til at give slip, men det er som om kroppen har sin egen velje og bare holder fast som den har gjort så længe. Det er helt OK - kroppen slipper når den er klar. Det kan være svært at slippe styringen og kontrollen, og lader det ske som skal ske.
            </blockquote>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ved afslutning af behandlingen kan man nogen gange føle en form for lethed - som om noget, der tyngede er væk. Det kan føles som om der er skabt rum indeni - Noget som var der er nu væk - Der er skabt plads til noget nyt. Det er ikke altid der kan sættes ord på hvad der var som tyngede - Det kan ikke måles og vejes, men en forandring mærkes inden i.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Behandlingen afsluttes evt. med en kort samtale, hvor du kan stille spørgsmål, og jeg komme med input til, hvordan du selv kan arbejde videre derhjemme i dagligdagen, enten gennem øvelser eller ting du kan være opmærksom på og bevidst om i dit møde med dine omgivelser.
            </p>
          </div>

          {/* Efter behandlingen & Udbytte */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Efter behandlingen
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Da vi effektivt går ind og arbejder med dine spændinger i muskler, sener og led, kan der i dagene efter en behandling forekomme ømhed i kroppen. Det er helt normalt og kan sammenlignes med ømheden efter en omgang sund træning og motion. Sørg for at drikke godt med vand, så kroppen får de bedste betingelser for at udskille de affaldsstoffer, der er blevet frigjort under behandlingen.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Har du gennem længere tid ignoreret din krops signaler, kan det efter behandlingen føles som om, at dine symptomer forværres. Det skal du ikke være bekymret over, da det er en helt normal efterreaktion på behandlingen, fordi kroppen arbejder videre i dagene efter.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ømheden kan nogle gange mærkes i 2-3 dage eller længere tid efter behandlingen. Du kan desuden opleve psykiske/følelsesmæssige reaktioner alt afhængig af, hvad vi har arbejdet med. - Prøv at være nysgerrig og mærk efter hvad det er kroppen forsøger at fortælle dig.
            </p>
            <p className="text-xs text-[#64748B]">
              TOTUM Kropsterapi skal ikke træde i stedet for anden medicinsk behandling, men kan fint kombineres med eventuel medicin eller samtaleterapi.
            </p>
          </div>

          {/* Dit udbytte & Slogan */}
          <div className="bg-[#1b3a4b] text-white rounded-2xl p-8 sm:p-10 shadow-lg space-y-4">
            <h3 className="font-serif text-2xl font-bold text-white">
              Dit udbytte af en behandling
            </h3>
            <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
              Behandlingen kan skaber en kæde af følelsesmæssige, mentale og fysiske reaktioner hos dig. Det at finde modet til at møde og turde slippe fortrængte følelser, gør dig i stand til at bruge dine mentale ressourcer mere konstruktivt. Som din kropsterapeut er det vigtigt for mig at du føler dig tryg og ikke ladt alene i processen.
            </p>
            <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
              TOTUM Kropsterapi hjælper dig med at mærke det som fylder indeni - følelser som kan være svære at sætte ord på, bliver bearbejdet og sluppet fri. Opmærksomheden på din krop og dine følelser giver en bedre jordforbindelse, og et fysisk overskud når åndedrættet sættes fri.
            </p>
            <div className="pt-4 border-t border-white/20 text-center">
              <span className="font-serif text-lg sm:text-xl font-bold text-[#7c875a] block">
                Dit Liv - Dine Muligheder - Dine Valg - Dit Ansvar - Din Frihed
              </span>
            </div>
          </div>
        </div>

        {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6" id="kontakt-sektion">
              <ContactForm preselectedInterest="Kropsterapi" />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
